/**
 * Answer Feedback Component
 *
 * Shows immediate feedback after user submits an answer
 * Allows exploring words in explanations and answers
 */

import React from 'react';
import { AnswerState } from '../../types/grammarPractice';
import { parseExplanation } from '../../utils/explanationParser';
import { UserOptions } from '../../types/chat';
import ClickableText from '../common/ClickableText';

interface AnswerFeedbackProps {
  answerState: AnswerState;
  onNext: () => void;
}

const AnswerFeedback: React.FC<AnswerFeedbackProps> = ({ answerState, onNext }) => {
  if (!answerState.submitted) {
    return null;
  }

  // Get user's native language from localStorage
  const getNativeLanguage = (): string => {
    try {
      const userOptionsStr = localStorage.getItem('userOptions');
      if (userOptionsStr) {
        const userOptions: UserOptions = JSON.parse(userOptionsStr);
        return userOptions.user_native_language || 'en-US';
      }
    } catch (error) {
      console.error('Error reading native language from localStorage:', error);
    }
    return 'en-US'; // Default fallback
  };

  const nativeLanguage = getNativeLanguage();
  const parsedExplanation = parseExplanation(answerState.explanation, nativeLanguage);

  return (
    <div className="pointer-events-none absolute inset-x-0 -top-6 flex justify-center px-4 z-20">
      <div
        className={`pointer-events-auto w-full max-w-xl alert ${
          answerState.isCorrect ? 'alert-success' : 'alert-error'
        } shadow-xl`}
        role="status"
        aria-live="assertive"
      >
        <div className="flex flex-col w-full">
          <div className="flex items-center gap-2">
            {answerState.isCorrect ? (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="stroke-current shrink-0 h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="font-bold">Correct!</span>
              </>
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="stroke-current shrink-0 h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="font-bold">Incorrect</span>
              </>
            )}
          </div>

          {!answerState.isCorrect && answerState.correctAnswer && (
            <div className="mt-2 space-y-1 text-sm">
              <p>
                <strong>Your answer:</strong> <ClickableText text={answerState.userAnswer} />
              </p>
              <p>
                <strong>Correct answer:</strong>{' '}
                <ClickableText text={answerState.correctAnswer} />
              </p>
            </div>
          )}

          {parsedExplanation && (
            <div className="mt-2 text-sm">
              <p>
                <strong>Explanation:</strong>{' '}
                <ClickableText text={parsedExplanation} />
              </p>
            </div>
          )}

          <div className="flex justify-end mt-3">
            <button className="btn btn-sm btn-primary" onClick={onNext}>
              Next Question
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnswerFeedback;
